// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngCarousel from "@eisinc/App/Components/XngCarousel";
import LazyImage, { LazyImageProps } from "@eisinc/MobiusOverrides/LazyImage";
import { constants } from "@eisinc/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showImages = "showImages",
}

interface Props extends WidgetProps, HasCategoryContext {}

export interface CategoryDetailSubCategoriesStyles {
    container?: GridContainerProps & InjectableCss;
    innerContainer?: GridContainerProps;
    // xng-changes
    categoryImage?: LazyImageProps;
    wrapperCategoryLink?: InjectableCss;
    categoryText?: TypographyPresentationProps;
    categoryImageWrapper?: InjectableCss;
}

export const categoryDetailSubCategoriesStyles: CategoryDetailSubCategoriesStyles = {
    container: {
        gap: 0,
        css: css`
            position: relative;
            overflow: hidden;
            display: flex;
            flex-wrap: wrap;
            transition: max-height 400ms ease-out;
        `,
    },
};

const styles = categoryDetailSubCategoriesStyles;

const CategoryDetailSubCategories = ({ category, fields }: Props) => {
    if (!category || !category.subCategoryIds || category.subCategoryIds.length === 0) {
        return null;
    }

    useEffect(() => {
        typeof window !== "undefined" &&
            typeof localStorage !== "undefined" &&
            window?.localStorage?.setItem(constants.localStorage.shouldLoadProductsForSubCategories, "true");
    }, []);

    return (
        <XngCarousel
            scrollbar={true}
            spaceBetween={24}
            slideWidth="148px"
            draggable={IS_SERVER_SIDE ? false : isTouchDevice()}
            navigation={true}
        >
            {category.subCategoryIds.length &&
                category.subCategoryIds.map(subCategoryId => (
                    <SubCategoryLink key={subCategoryId} categoryId={subCategoryId} showImages={fields.showImages} />
                ))}
        </XngCarousel>
    );
};

const SubCategoryLinkView = ({
    category,
    showImages,
    settingsCollection,
}: ReturnType<typeof mapStateToProps> & { showImages: boolean }) => {
    if (!category) {
        return null;
    }

    const customSettings = settingsCollection["customSettings"] as any;

    return (
        <Clickable href={category.path} {...styles.wrapperCategoryLink}>
            <StyledWrapper {...styles.categoryImageWrapper}>
                {showImages && (
                    <LazyImage
                        src={
                            category.smallImagePath
                                ? category.smallImagePath
                                : customSettings?.mediumDefaultCategoryImage
                        }
                        altText={category.imageAltText}
                        {...styles.categoryImage}
                    ></LazyImage>
                )}
            </StyledWrapper>
            <Typography
                color="text.main"
                {...styles.categoryText}
                data-test-selector={`categoryDetailsSubCategoriesLink_${category.id}`}
            >
                {category.shortDescription}
            </Typography>
        </Clickable>
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
    settingsCollection: getSettingsCollection(state),
});

const SubCategoryLink = connect(mapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: withCategory(CategoryDetailSubCategories),
    definition: {
        group: "Category Details",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
